import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles'
import AnimateHeight from 'react-animate-height';

import { expressInterest, unexpressInterest } from '../../../actions/scheduling';
import { withRtl } from '../../../components/hoc';
import ScheduleButton from '../ScheduleButton';
import Button from '../../../components/Button';
import Loader from '../../../components/Loader';
import messages from '../../../locale/messages';
import style from '../ScheduleForm/ScheduleForm.scss'; // eslint-disable-line css-modules/no-unused-class
import { 
  INSTUMENTAL_MUSIC_PROGRAM_CODE, 
  MT_EXPRESSED_INTEREST_PROGRAMS,
  MT_AUDITION_PROGRAMS 
} from '../../../constants';

import ExpressedInterestSaturdaySundayStep from './ExpressedInterestSaturdaySundayStep';
import ExpressedInterestSelectInstrumentsStep from './ExpressedInterestSelectInstrumentsStep';
import ExpressInterestSubmitStep from './ExpressInterestSubmitStep';
import ExpressInterestFormError from './ExpressInterestFormError';
import ExpressInterestStepContainer from './ExpressInterestStepContainer';
import { saveApplication } from '../../../actions/saveApplication';
import { groupUpdateApplicationRank } from '../../../actions/updateApplicationRank';
import { showPopupContent } from '../../../actions/popup';
import {customisedIntlFormatMessage} from '../../../utils/helpers';

const STEP_1 = 'accommodations';
const STEP_2 = 'express';
const STEP_SATURDAY_SUNDAY = 'saturdaySundayOption';
const STEP_SELECT_INSTRUMENTS = 'selectInstruments';
const CANCEL_STEP = 'cancel';

const ANIMATION_DURATION = 700;

/* eslint-disable css-modules/no-undef-class */
const localMessages = defineMessages({
  expressionOfInterestSuccess: {
    id: 'schedule.expressionOfInterest.Success',
    defaultMessage: 'You have successfully registered for testing.',
    description: 'The schedule expressed interest success message',
  },
  expressionOfInterestSuccessForMTAudition: {
    id: 'schedule.expressionOfInterest.SuccessForMTAudition',
    defaultMessage: 'You have successfully expressed interest.',
    description: 'The schedule expressed interest success message',
  },
  expressionOfInterestSuccessLaGuardia: {
    id: 'schedule.expressionOfInterest.SuccessLaGuardia',
    defaultMessage: 'You have successfully added the instrument(s) for the auditions!',
    description: 'Saving instruments data success message',
  },
  expressionOfInterestInfo: {
    id: 'schedule.expressionOfInterest.Info',
    defaultMessage:
      'Click Submit Registration below to register. Test/audition tickets will be available in mid-October.',
    description: 'The schedule expressed interest info message',
  },
  expressionOfInterestInfoHighlighted: {
    id: 'schedule.expressionOfInterest.InfoHighlighted',
    defaultMessage: 'This program only requires that you express your interest.',
    description: 'The schedule expressed interest highlighted info message',
  },
  expressionOfInterestInfoLaGuardiaCase: {
    id: 'schedule.expressionOfInterest.InfoLaGuardiaCase',
    defaultMessage: 'Submit Registration below to register. Test/audition tickets will be available in mid-October.',
    description: 'The schedule LaGuardia interest info message',
  },
  expressionOfInterestRemove: {
    id: 'schedule.expressionOfInterest.Remove',
    defaultMessage: 'Are you sure you want to remove your expression of interest?',
    description: 'The schedule expressed interest removal message',
  },
  expressionOfInterestRemoveMS: {
    id: 'schedule.expressionOfInterest.RemoveMS',
    defaultMessage: 'Are you sure you want to unregister for testing?',
    description: 'The Middle School schedule expressed interest removal message',
  },
  expressionOfInterestRemoveMSAudtionProgram: {
    id: 'schedule.expressionOfInterest.RemoveMSAudtionProgram',
    defaultMessage: 'Are you sure you want to withdraw interest?',
    description: 'The Middle School schedule expressed interest removal message',
  },
  expressionOfInterestRemoveLaGuardia: {
    id: 'schedule.expressionOfInterest.RemoveLaGuardia',
    defaultMessage: 'Are you sure you want cancel your audition?',
    description: 'The schedule cancel audition message',
  },
  program504AccommodationsBeforeTooltip: {
    id: 'schedule.Program504AccommodationsBeforeTooltip',
    defaultMessage: 'Does {name} have a',
    description: 'The text before the 504 accommodations tooltip',
  },
  program504AccommodationsTooltip: {
    id: 'schedule.Program504AccommodationsTooltip',
    defaultMessage:
      'A 504 Plan identifies allergies, injuries, and other factors that may require a testing accommodation.',
    description: 'The text in the 504 accommodations tooltip',
  },
  program504AccommodationsTooltipLabel: {
    id: 'schedule.Program504AccommodationsTooltipLabel',
    defaultMessage: '504 Plan?',
    description: 'The label for the 504 accommodations tooltip',
  },
  programAccommodationsEmptyTextTop: {
    id: 'schedule.ProgramAccommodationsEmptyTextTop',
    defaultMessage: "We understand that you don't require any accommodations for testing.",
    description: 'The program accommodations empty top text',
  },
  programAccommodationsTextTop: {
    id: 'schedule.ProgramAccommodationsTextTop',
    defaultMessage: 'The following accommodations are available to you:',
    description: 'The program accommodations top text',
  },
  programAccommodationsTextBottom: {
    id: 'schedule.ProgramAccommodationsTextBottom',
    defaultMessage:
      "If this information is incorrect or your child has a 504 Plan, contact your child's school counselor.",
    description: 'The program accommodations bottom text',
  },
  programAccommodationsEmptyTitle: {
    id: 'schedule.ProgramAccommodationsEmptyTitle',
    defaultMessage: 'With no accommodations',
    description: 'The empty program accommodations title',
  },
  programAccommodationsTitle: {
    id: 'schedule.ProgramAccommodationsTitle',
    defaultMessage: 'With use of {accommodations}',
    description: 'The program accommodations title',
  },
  programAccommodationsVerified504: {
    id: 'schedule.ProgramAccommodationsVerified504',
    defaultMessage: 'You have a verified 504 Plan.',
    description: 'The program accommodations 504 verified plan message',
  },
  programAccommodationsNoIEPELL: {
    id: 'schedule.programAccommodationsNoIEPELL',
    defaultMessage: 'We understand that you have the following accommodations',
    description: 'The string shown if user has no IEP nor ELL accommodations',
  },
  programAccommodationsIEP: {
    id: 'schedule.programAccommodationsIEP',
    defaultMessage:
      'We understand that you have testing accommodations because you have an ' +
      'Individualized Education Program (IEP).',
    description: 'The string shown if user has only IEP accommodations',
  },
  programAccommodationsELL: {
    id: 'schedule.programAccommodationsELL',
    defaultMessage:
      'We understand that you have testing accommodations because you are an ' +
      'English Language Learner or a former English Language Learner who has achieved ' +
      'proficiency on the NYSESLAT within the past two years.',
    description: 'The string shown if user has only ELL accommodations',
  },
  programAccommodationsIEPELL: {
    id: 'schedule.programAccommodationsIEPELL',
    defaultMessage:
      'We understand that you have an Individualized Education Program (IEP) and/or you have ' +
      'testing accommodations because you are an English Language Learner or a former English ' +
      'Language Learner who has achieved proficiency on the NYSESLAT within the past two years.',
    description: 'The string shown if user has both IEP and ELL accommodations',
  },
  programAccommodationsInfo: {
    id: 'schedule.ProgramAccommodationsInfo',
    defaultMessage:
      'Accommodations may include any instructional accessibility needs or extenuating ' +
      'circumstances that would require accommodating.',
    description: 'The text under Accommodations header',
  },
  saturdaySundayOptionTitle: {
    id: 'schedule.SaturdaySundayOptionTitle',
    defaultMessage: 'Select a day preference',
    description: 'The text for Saturday/Sunday step title',
  },
  noDayPreferenceTitle: {
    id: 'schedule.NoDayPreferenceTitle',
    defaultMessage: 'Testing Day',
    description: 'The text for Saturday/Sunday step title when school has a mandatory testing day',
  },
  noDayPreferenceMessage: {
    id: 'schedule.NoDayPreferencePreference',
    defaultMessage:
      'Students who attend your child’s school will take the SHSAT during the school day at their school on Wednesday, October 30, 2019',
    description: 'The text that replaces Saturday/Sunday checkboxes when school has a mandatory testing day',
  },
  selectInstrumentsTitle: {
    id: 'schedule.SelectInstrumentsOptionTitle',
    defaultMessage: 'Select Instrument(s)',
    description: 'The text for Select Instruments step title',
  },
  selectInstrumentsError: {
    id: 'schedule.SelectInstrumentsError',
    defaultMessage: 'Please, select your first instrument',
    description: 'Error text for Select Instruments step title',
  },
  selectInstrumentOption: {
    id: 'schedule.SelectOption',
    defaultMessage: 'Select',
    description: 'Default option string for dropdowns',
  },
  msRegisterCopy: {
    id: 'schedule.msRegisterCopy',
    defaultMessage:
      'You will receive a date and time(s) for your talent test(s) prior to the exam date.',
    description: 'Copy for final step of MS registration',
  },
});

class ExpressedInterestForm extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      currentStep: STEP_1,
      accommodation504: false,
      saturdaySundayOption: 'NL',
      accommodationStepPassed: false,
      selectInstrumentsStepPassed: false,
      saturdaySundayStepPassed: false,
      expressed: false,
      cancel: false,
      selectInstrumentsOptionOne: { instrument_id: 0, description: customisedIntlFormatMessage(props.intl,  localMessages.selectInstrumentOption) },
      selectInstrumentsOptionTwo: null,
      selectInstrumentsError: false,
    };    
    if(props.isLG){
      this.state = { ...this.state, currentStep: STEP_SELECT_INSTRUMENTS };
    }

    this.stepMapping = {
      [STEP_1]: this.renderAccommodations,
      [STEP_SELECT_INSTRUMENTS]: this.renderSelectInstruments,
      [STEP_SATURDAY_SUNDAY]: this.renderSaturdaySunday,
      [STEP_2]: this.renderExpress,
    };

    this.stepMappingMS = {
      [STEP_1]: this.renderAccommodations,
      [STEP_SATURDAY_SUNDAY]: this.renderSaturdaySunday,
      [STEP_2]: this.renderExpress,
    };

    if (props.program.schedule && props.program.schedule.expression_of_interest.id) {
      this.state = { ...this.state, cancel: true };
    }
    this.accommodations = props.user.details.data.accommodations;
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.expressInterestForm.result.id && nextProps.expressInterestForm.result.id) {
      this.setState({ expressed: true });
    }
  }

  getStepButtonClasses = step => {
    const stepButtonClasses = ['button--no-style', style.scheduleFormStepButton];

    if (this.isCurrentStep(step)) {
      stepButtonClasses.push(style.scheduleFormStepButtonActive);
    } else if (this.isCurrentStep(CANCEL_STEP)) {
      stepButtonClasses.push(style.scheduleFormStepButtonDone);
    }

    return { stepButtonClasses };
  };

  getFinalStepTitle = () => {
    const { user, program } = this.props;
    let finalStepTitle = <FormattedMessage {...messages.buttonExpressInterest} />;
    if (this.isLaGuardia()) {
      finalStepTitle = <FormattedMessage {...messages.buttonExpressInterestLaGuardiaCase} />;
    } else if (user.isMS) {
      if(MT_AUDITION_PROGRAMS.includes(program.schedule.name))
        finalStepTitle = <FormattedMessage {...messages.buttonExpressInterest} />;
      else
        finalStepTitle = <FormattedMessage {...messages.buttonExpressInterestMSNonAudition} />;
    }
    return finalStepTitle;
  };

  getRemoveExpressionOfInterestFormTitle = () => {
    const { user, program } = this.props;
    if (this.isLaGuardia()) {
      return <FormattedMessage {...localMessages.expressionOfInterestRemoveLaGuardia} />;
    } else if (user.isMS) {
      return program.schedule.confirmatory_message;
    }

    return <FormattedMessage {...localMessages.expressionOfInterestRemove} />;
  };

  expressInterest = () => {
    const { applicationId, program, user, programToBeSaved, dispatch } = this.props;
    const { selectInstrumentsOptionOne, selectInstrumentsOptionTwo } = this.state;
    let data = {
      student_application_id: applicationId,
    };
    if(!user.isLG)
      data = {student_application_id: applicationId, schedule_id: program.schedule.id}
    data = { ...data, saturday_sunday_option: this.state.saturdaySundayOption };
    let code = '';
    if(user.isLG)
      code = program.program.code;
    else
      code = program.code;
    if (code === INSTUMENTAL_MUSIC_PROGRAM_CODE) {
      if (selectInstrumentsOptionTwo !== null) {
        data = { ...data, instruments: [selectInstrumentsOptionOne, selectInstrumentsOptionTwo] };
      } else {
        data = { ...data, instruments: [selectInstrumentsOptionOne] };
      }
    }
      this.props.expressInterest(applicationId, data);
      if(user.isLG && code === INSTUMENTAL_MUSIC_PROGRAM_CODE && programToBeSaved){
        dispatch(saveApplication(programToBeSaved.programId, programToBeSaved.schoolId, '', programToBeSaved.savedApplication, programToBeSaved.savedApplicationId, programToBeSaved.programData)).then(() => {
          this.storeProgramRanking();
        });

        dispatch(
          showPopupContent({
            children: 'This program has been added to your application',
          }),
        );
        this.setState({ expressed: true });
      }
      //save program here

  };

  unexpressInterest = () => {
    const { applicationId, program } = this.props;
    if (program.schedule && program.schedule.expression_of_interest) {
      this.props.unexpressInterest(applicationId, program.schedule.expression_of_interest.id);
    }
  };

  close = () => {
    this.props.reset();
  };

  isCurrentStep = step => this.state.currentStep === step;

  allStepsPassed = () => this.state.currentStep === CANCEL_STEP;

  accommodationPassed = () => this.state.accommodationStepPassed;

  saturdaySundayPassed = () => this.state.saturdaySundayStepPassed;

  selectInstrumentsPassed = () => this.state.selectInstrumentsStepPassed;

  isLaGuardia = () => {
    const { program, isLG } = this.props;
    if (isLG) {
      return true;
    }
    return false;
  };

  accommodation504Changed = event => {
    this.setState({
      ...this.state,
      accommodation504: event.target.checked,
    });
  };

  saturdaySundayValueChanged = event => {
    this.setState({
      ...this.state,
      saturdaySundayOption: event.target.value,
    });
  };

  selectInstrumentsValueChanged = (key, id, value) => {
    this.setState({
      ...this.state,
      [`selectInstrumentsOption${key}`]: { instrument_id: Number(id), description: value },
    });
  };

  handleNextStep = () => {
    const { selectInstrumentsOptionOne } = this.state;
    const nextStep = STEP_2;
    if (selectInstrumentsOptionOne.description === 'SELECT') {
      this.setState({ ...this.state, selectInstrumentsError: true });
    } else {
      this.setState({
        ...this.state,
        currentStep: nextStep,
        selectInstrumentsPassed: true,
      });
    }
  };

  renderError = () => (
    <div className={style.scheduleFormError}>
      <FormattedMessage {...messages.generalStringSomethingWentWrong} />
    </div>
  );

  renderAccommodations = (show = true) => {
    const { user, intl, program, isMS } = this.props;
    let nextStep;
    let code='';
    if(user.isMS)
      code = program.school.name;
    else if(!user.isLG)
      code = program.code;
    else
      code = program.program.code;
    
    if (code === INSTUMENTAL_MUSIC_PROGRAM_CODE) {
      nextStep = STEP_SELECT_INSTRUMENTS;
    }
    else if (isMS && MT_EXPRESSED_INTEREST_PROGRAMS.includes(code)){
      nextStep = STEP_SATURDAY_SUNDAY;
    }
    else {
      nextStep = STEP_2;
    }
    // const accommodationsString = this.accommodations.map(accommodation => accommodation.name).join(', ');
    // const accommodationList = this.accommodations.map(accommodation => (
    //   <li key={accommodation.name}>{accommodation.name}</li>
    // ));
    let accommodationList = [];
    if(this.accomodations && this.accomodations.length > 0)
      accommodationList = this.accommodations.map(accommodation => (
        <li key={accommodation.name}>{accommodation.name}</li>
      ));

    const accommodationIEP = this.props.user.details.data.acc_iep;
    const accommodationELL = this.props.user.details.data.acc_ell;
    const accommodationOther = this.props.user.details.data.acc_other;
    const learnMoreLink =
      'http://schools.nyc.gov/Academics/SpecialEducation/FamilyResources/GuidesDocuments/default.htm';

    if (accommodationOther) {
      accommodationList.push(<li key={accommodationOther}>{accommodationOther}</li>);
    }

    const accommodationsContent = (
      <div className={style.scheduleFormAccommodationsEmpty}>
        <div className={style.scheduleFormAccommodationsText}>
          {this.accommodations && this.accommodations.length > 0 ? (
          // {this.accommodations.length > 0 ? (
            <Fragment>
              <p>
                {accommodationIEP && accommodationELL && (
                  <FormattedMessage {...localMessages.programAccommodationsIEPELL} />
                )}
                {accommodationIEP && !accommodationELL && (
                  <FormattedMessage {...localMessages.programAccommodationsIEP} />
                )}
                {!accommodationIEP && accommodationELL && (
                  <FormattedMessage {...localMessages.programAccommodationsELL} />
                )}
                {!accommodationIEP && !accommodationELL && (
                  <FormattedMessage {...localMessages.programAccommodationsNoIEPELL} />
                )}
                <a
                  href={learnMoreLink}
                  target="_blank"
                  rel="noopener"
                  className={style.scheduleFormLearnMore}
                  aria-label={`${customisedIntlFormatMessage(intl, messages.learnMoreLink)} ${customisedIntlFormatMessage(intl, 
                    messages.opensInNewWindow,
                  )}`}
                >
                  <FormattedMessage {...messages.learnMoreLink} />
                </a>
              </p>
              {(accommodationIEP || accommodationELL) && (
                <p>
                  <FormattedMessage {...localMessages.programAccommodationsTextTop} />
                </p>
              )}
              <ul>{accommodationList}</ul>
            </Fragment>
          ) : (
            <Fragment>
              <div dangerouslySetInnerHTML={{__html: sanitize_html(program.schedule.program_accommodations_description)}}></div>
              <p>
                <FormattedMessage {...localMessages.programAccommodationsEmptyTextTop} />
              </p>
            </Fragment>
          )}
        </div>
        {user.details.data.has_verified_504_plan && (
          <div
            className={classNames(
              style.scheduleFormAccommodationsText,
              style.scheduleFormAccommodationsVerified504,
            )}
          >
            <FormattedMessage {...localMessages.programAccommodationsVerified504} />
          </div>
        )}
        <div className={style.scheduleFormAccommodationsText}>
          <FormattedMessage {...localMessages.programAccommodationsTextBottom} />
        </div>
      </div>
    );

    let accommodation504;

    const buttons = (
      <div className={style.scheduleFormProgressButtonWrapper}>
        <Button
          label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
          onClick={() => {
            this.setState({ ...this.state, currentStep: nextStep, accommodationStepPassed: true });
          }}
        />
      </div>
    );

    let title = 'Accommodations';

    const isCurrentStep = this.isCurrentStep(STEP_2);

    if (!isCurrentStep) {
      // if (this.accommodations.length === 0) {
      if (this.accommodations && this.accommodations.length === 0) {
        title = customisedIntlFormatMessage(intl, localMessages.programAccommodationsEmptyTitle);
      }
    }

    const stepContent = (
      <AnimateHeight duration={ANIMATION_DURATION} height={show ? 'auto' : 0} animateOpacity>
        <div className={style.scheduleFormStepWrapper}>
          <div className={style.scheduleFormStep}>
            <div>
              {accommodationsContent}
              {accommodation504}
              {buttons}
            </div>
          </div>
        </div>
      </AnimateHeight>
    );
    const subtitle = (
      <span>
        <i className="icon-Star" /> {program.name}
      </span>
    );
    return (
      <div className={style.scheduleFormStepContainer}>
        {!user.isLG && <ExpressInterestStepContainer title={program.school.name} subtitle={subtitle} />}
        {user.isLG && <ExpressInterestStepContainer title={program.directory_school_name} subtitle={subtitle} />}
        <ExpressInterestStepContainer title={title} style={style} content={stepContent} active />
        {code === INSTUMENTAL_MUSIC_PROGRAM_CODE && (
          <ExpressInterestStepContainer title={customisedIntlFormatMessage(intl, localMessages.selectInstrumentsTitle)} />
        )}
        {(isMS && MT_EXPRESSED_INTEREST_PROGRAMS.includes(code)) && (
          <ExpressInterestStepContainer title={intl.formatMessage(localMessages.saturdaySundayOptionTitle)} />
        )}
        <ExpressInterestStepContainer title={this.getFinalStepTitle()} style={style} />
      </div>
    );
  };

  renderSaturdaySunday = (show = true) => {
    const { intl, program, user } = this.props;
    const schoolNameButtonClasses = ['button--no-style', style.scheduleFormStepButton];
    const { stepButtonClasses } = this.getStepButtonClasses(STEP_SATURDAY_SUNDAY);
    const nextStep = STEP_2;

    /* Need to check the student's current school DBN against the list below */
    const noDayPreference = [
      '01M83',
      '03M421',
      '04M057',
      '06M223',
      '06M324',
      '08X071',
      '08X101',
      '09X128',
      '09X568',
      '10X095',
      '10X118',
      '10X244',
      '11X083',
      '11X127',
      '11X180',
      '11X194',
      '11X556',
      '12X318',
      '14K050',
      '14K084',
      '14K157',
      '14K318',
      '14K577',
      '14K582',
      '16K057',
      '16K267',
      '17K002',
      '17K354',
      '18K211',
      '18K763',
      '19K089',
      '19K292',
      '22K278',
      '22K381',
      '23K392',
      '23K664',
      '24Q073',
      '27Q137',
      '27Q202',
      '28Q157',
      '29Q289',
      '30Q141',
      '30Q291',
      '31R051',
      '32K045',
      '32K162',
      '32K291',
      '32K349',
      '32K383',
      '32K562',
      '84K651',
      '84M100',
      '84M330',
      '84M430',
      '84X487',
    ];
    // 25Q252 (current school)
    /* Use this array to get a positive test result * /
    const noDayPreference = [ '01M83', '03M421', '04M057', '06M223', '06M324', '08X071', '08X101', '09X128', '09X568', '10X095', '10X118', '10X244', '11X083', '11X127',    '11X180', '11X194', '11X556', '12X318', '14K050', '14K084', '14K157', '14K318', '14K577', '14K582', '16K057', '16K267', '17K002', '17K354', '18K211', '18K763',   '19K089', '19K292', '22K278', '22K381', '23K392', '23K664', '24Q073', '27Q137', '27Q202', '28Q157', '29Q289', '30Q141', '30Q291', '31R051', '32K045', '32K162',   '32K291', '32K349', '32K383', '32K562', '84K651', '84M100', '84M330', '84M430', '84X487',
    '11X061P'];
    */
    /* If the current school is in this list, make the following changes to exclude the Saturday/Sunday option */
    let schoolDbn = '';
    if(user.isLG)
      schoolDbn = program.directory_school_dbn;
    else
      schoolDbn = program.school.dbn;

    const dayPreferenceUnavailable =
    noDayPreference.includes(user.details.data.current_school.dbn) && schoolDbn !== '03M485';
    const title = dayPreferenceUnavailable
      ? customisedIntlFormatMessage(intl, localMessages.noDayPreferenceTitle)
      : customisedIntlFormatMessage(intl, localMessages.saturdaySundayOptionTitle);
    const alternateMessage = dayPreferenceUnavailable ? customisedIntlFormatMessage(intl, localMessages.noDayPreferenceMessage) : null;
    return (
      <ExpressedInterestSaturdaySundayStep
        program={program}
        intl={intl}
        schoolNameButtonClasses={schoolNameButtonClasses}
        stepButtonClasses={stepButtonClasses}
        title={title}
        show={show}
        nextClicked={() => {
          this.setState({
            ...this.state,
            currentStep: nextStep,
            saturdaySundayStepPassed: true,
          });
        }}
        selectionChanged={this.saturdaySundayValueChanged}
        selectedOption={this.state.saturdaySundayOption}
        alternateMessage={alternateMessage}
      />
    );
  };

  renderSelectInstruments = (show = true) => {
    const { intl, program, expressedInterestsInstruments } = this.props;
    const schoolNameButtonClasses = ['button--no-style', style.scheduleFormStepButton];
    const { stepButtonClasses } = this.getStepButtonClasses(STEP_SELECT_INSTRUMENTS);
    const title = customisedIntlFormatMessage(intl, localMessages.selectInstrumentsTitle);
    let titleAccommodation = 'Accommodations';
    if(this.accomodations === undefined){
      titleAccommodation = <FormattedMessage {...localMessages.programAccommodationsEmptyTitle} />;
    } 
    if (this.accommodations && this.accommodations.length === 0) {
      titleAccommodation = <FormattedMessage {...localMessages.programAccommodationsEmptyTitle} />;
    }    
    return (
      <ExpressedInterestSelectInstrumentsStep
        program={program}
        intl={intl}
        schoolNameButtonClasses={schoolNameButtonClasses}
        stepButtonClasses={stepButtonClasses}
        title={title}
        show={show}
        nextClicked={this.handleNextStep}
        selectionChanged={this.selectInstrumentsValueChanged}
        selectedOptionOne={this.state.selectInstrumentsOptionOne}
        selectedOptionTwo={this.state.selectInstrumentsOptionTwo}
        firstInstrumentName="One"
        secondInstrumentName="Two"
        instrumentsOptions={expressedInterestsInstruments.result}
        error={this.state.selectInstrumentsError}
        errorMessage={localMessages.selectInstrumentsError}
        titleAccommodation={titleAccommodation}
        isLaGuardia={this.isLaGuardia()}
      />
    );
  };

  renderExpress = () => {
    const { program, expressInterestForm, intl, user } = this.props;
    const { expressed } = this.state;
    return (
      <ExpressInterestSubmitStep
        expressed={expressed}
        expressInterestForm={expressInterestForm}
        expressedInterestsCount={this.props.expressedInterests.result.length}
        intl={intl}
        isMS={user.isMS}
        isLaGuardia={this.isLaGuardia()}
        localMessages={localMessages}
        onAccomodationStepClick={() => this.setState({ ...this.state, currentStep: STEP_1 })}
        onSubmit={this.expressInterest}
        program={program}
      />
    );
  };

  storeProgramRanking = () => {
    const { dispatch } = this.props;
    const { results } = this.props.user.studentApplications;
    const { application } = this.props.user.studentApplications.entities;

    const y = results.map((id, index) => ({
      id: application[id].id,
      rank: index + 1,
    }));
    dispatch(groupUpdateApplicationRank(y));
  };


  renderCancel = () => {
    const { program, unexpressInterestForm, user, applicationId, intl } = this.props;
    const programId = program.directory_program ? program.directory_program.id : null;
    const studentApplications = user.studentApplications.results;

    if (programId && studentApplications.includes(programId)) {
      return (
        <div>
          <div>
            <div className={style.scheduleFormScheduleTitleContainer}>
              <div className={style.scheduleFormScheduleTitle}>
                <FormattedMessage {...messages.programScheduleFurtherActionTitle} />
              </div>
            </div>
            <div>
              <div
                className={classNames(
                  style.scheduleFormScheduleSubtitle,
                  style.scheduleFormScheduleSubtitleFurtherAction,
                )}
              >
                <FormattedMessage {...messages.programScheduleFurtherAction} />
              </div>
              <ScheduleButton
                label={customisedIntlFormatMessage(intl, messages.goToApplicationsButton)}
                onClick={() => {
                  window.location = `/${intl.locale}/dashboard/${applicationId}/apply`;
                }}
              />
            </div>
          </div>
        </div>
      );
    }
    let schoolName = '';
    if(user.isLG)
      schoolName = program.directory_school_name;
    else
      schoolName = program.school.name;
    return (
      <div>
        <div>
          <div className={style.scheduleFormScheduleTitleContainer}>
            <div className={style.scheduleFormScheduleTitle}>
              {this.getRemoveExpressionOfInterestFormTitle()}
            </div>
          </div>
          <div className={style.scheduleFormStepContainer}>
            <div className={style.scheduleFormStepTitleContainer}>
              <button
                disabled
                aria-label={schoolName}
                // aria-label={program.directory_school_name}
                className={classNames(
                  'button--no-style',
                  style.scheduleFormStepButton,
                  style.scheduleFormStepButtonDone,
                )}
              >
                <div className={style.scheduleFormStepButtonIcon}>
                  <i className="icon-Check" />
                </div>
                <div className={style.scheduleFormStepTitle}>
                  <div>{schoolName}</div>
                  {/* <div>{program.directory_school_name}</div> */}
                  <div>
                    <i className="icon-Star" /> {program.name}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
        <div className={style.scheduleFormCancelButtonContainer}>
          <ScheduleButton
            aria-label={customisedIntlFormatMessage(intl, messages.buttonYesRemove)}
            label={customisedIntlFormatMessage(intl, messages.buttonYesRemove)}
            icon="Check"
            onClick={this.unexpressInterest}
          />
          <span className={style.scheduleFormCancelButton}>
            <ScheduleButton secondary label={`${customisedIntlFormatMessage(intl, messages.buttonNoGoBack)}`} onClick={this.close} />
          </span>
        </div>
        {unexpressInterestForm.error && <ExpressInterestFormError />}
      </div>
    );
  };

  render() {
    const { expressInterestForm, unexpressInterestForm, user } = this.props;
    const { cancel, currentStep } = this.state;

    let renderedContent = null;

    // enforce limit of two ms schedules per application
    // should skip to submit step if limit is reached
    // eslint-disable-next-line no-unused-vars
    let skipToSubmitStep;
    const expressedInterestsCount = this.props.expressedInterests.result.length;

    if (user.isMS && expressedInterestsCount >= 2) {
      skipToSubmitStep = true;
    }

    if (expressInterestForm.submitting || unexpressInterestForm.submitting) {
      renderedContent = <Loader />;
    } else if (cancel) {
      renderedContent = this.renderCancel();
    } else if (currentStep === STEP_1) {
      renderedContent = this.renderAccommodations();
    } else if (currentStep === STEP_SATURDAY_SUNDAY) {
      renderedContent = this.renderSaturdaySunday();
    } else if (currentStep === STEP_SELECT_INSTRUMENTS) {
      renderedContent = this.renderSelectInstruments();
    } else if (currentStep === STEP_2) {
      renderedContent = this.renderExpress();
    }

    return <div className={style.scheduleFormContainer}>{renderedContent}</div>;
  }
}

ExpressedInterestForm.propTypes = {
  user: PropTypes.shape().isRequired,
  applicationId: PropTypes.string.isRequired,
  program: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  expressInterestForm: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  expressedInterests: PropTypes.shape().isRequired,
  unexpressInterestForm: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  reset: PropTypes.func.isRequired,
  expressInterest: PropTypes.func.isRequired,
  unexpressInterest: PropTypes.func.isRequired,
  isSHS: PropTypes.bool.isRequired,
  isLG: PropTypes.bool.isRequired,
  intl: PropTypes.shape({
    locale: PropTypes.string,
    messages: PropTypes.shape({}),
    formatMessage: PropTypes.func,
  }).isRequired,
  expressedInterestsInstruments: PropTypes.shape({
    result: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  programToBeSaved: PropTypes.object,
};

const mapState = state => ({
  expressInterestForm: state.expressInterestForm,
  unexpressInterestForm: state.unexpressInterestForm,
  expressedInterestsInstruments: state.expressedInterestsInstruments,
});

const mapDispatch = {
  expressInterest,
  unexpressInterest,
};

export default withRtl(style)(
  injectIntl(
    connect(
      mapState,
      mapDispatch,
    )(withStyles(style, style)(ExpressedInterestForm)),
  ),
);
